import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, PlusCircle, Pencil, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import ApiService from '@/services/api.service';
import BackgroundService from '@/services/bg.service';
import { toastService } from '@/services/toast.service';

interface PublishChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Phase = 'planning' | 'review' | 'publishing';

interface DiffItem {
  name?: string;
  label?: string;
  _id?: string;
  node_type?: string;
}

interface ModelDiff {
  created: DiffItem[];
  updated: DiffItem[];
  deleted: DiffItem[];
  has_model_changes: boolean;
}

interface PublishPlan {
  next_version_idx: number;
  diff: Record<string, ModelDiff>;
  has_any_changes: boolean;
  total_created: number;
  total_updated: number;
  total_deleted: number;
}

const MODEL_TYPE_LABELS: Record<string, string> = {
  PipelineNodeRelationship: 'Relationship',
  Visualization: 'Visualization',
  Dashboard: 'Dashboard',
  Dimension: 'Dimension',
  Measure: 'Measure',
  Analysis: 'Analysis',
  DashboardSuperset: 'Dashboard Dataset',
};

const NODE_TYPE_LABELS: Record<string, string> = {
  SOURCE: 'Source',
  SOURCE_FILE: 'Source File',
  SEED: 'Seed',
  SEED_SOURCE: 'Seed Source',
  STAGING: 'Staging',
  BUSINESS_OBJECT: 'Business Object',
  CUSTOM: 'Custom',
  STACK: 'Stack',
  MERGE: 'Merge',
  DEDUPLICATE: 'Deduplicate',
  SPLIT: 'Split',
  SUMMARIZE: 'Summarize',
  FACT: 'Fact',
};

function getItemName(item: DiffItem): string {
  return item.name || item.label || item._id || 'Unnamed';
}

function getItemType(modelType: string, item: DiffItem): string {
  if (modelType === 'PipelineNode' && item.node_type) {
    return NODE_TYPE_LABELS[item.node_type] || item.node_type;
  }
  return MODEL_TYPE_LABELS[modelType] || modelType;
}

function ChangedItemList({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  items: { name: string; type: string }[];
}) {
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        {icon}
        {title}
      </div>
      <div className="space-y-1 pl-6">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span>{item.name}</span>
            <Badge variant="outline" className="text-xs">
              {item.type}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PublishChangesDialog({ open, onOpenChange }: PublishChangesDialogProps) {
  const [phase, setPhase] = useState<Phase>('planning');
  const [plan, setPlan] = useState<PublishPlan | null>(null);
  const [publishMessage, setPublishMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setPhase('planning');
      setPlan(null);
      setPublishMessage('');
      setError(null);
      fetchPlan();
    }
  }, [open]);

  const fetchPlan = async () => {
    try {
      const resp = (await ApiService.getInstance().request('POST', '/draftversions/plan')) as {
        record: PublishPlan;
      };
      setPlan(resp.record);
      setPhase('review');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate publish plan';
      setError(message);
      toastService.error('Failed to generate publish plan', { description: message });
      onOpenChange(false);
    }
  };

  const handlePublish = async () => {
    if (!plan) return;

    setPhase('publishing');
    setError(null);

    try {
      const resp = (await ApiService.getInstance().request('POST', '/draftversions/publish', {
        next_version_idx: plan.next_version_idx,
        publish_message: publishMessage,
      })) as { job_id: string };

      await BackgroundService.getInstance().waitForJob(resp.job_id);

      // Exit draft mode
      localStorage.removeItem('draft_version_id');
      queryClient.setQueryData(['draft_version_id'], null);
      queryClient.invalidateQueries();

      toastService.success('Changes published successfully');
      onOpenChange(false);
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish changes';
      toastService.error('Publish failed', { description: message });
      setError(message);
      setPhase('review');
    }
  };

  const collectItems = (changeType: 'created' | 'updated' | 'deleted') => {
    if (!plan) return [];
    const items: { name: string; type: string }[] = [];
    for (const [modelType, modelDiff] of Object.entries(plan.diff)) {
      for (const item of modelDiff[changeType]) {
        items.push({
          name: getItemName(item),
          type: getItemType(modelType, item),
        });
      }
    }
    return items;
  };

  const isBlocked = phase === 'publishing';

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!isBlocked) onOpenChange(value);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        onEscapeKeyDown={(e) => {
          if (isBlocked) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isBlocked) e.preventDefault();
        }}
        hideClose={isBlocked}
      >
        <DialogHeader>
          <DialogTitle>Publish Changes</DialogTitle>
          <DialogDescription>
            {phase === 'planning' && 'Analyzing your changes...'}
            {phase === 'review' && 'Review your changes before publishing to production.'}
            {phase === 'publishing' && 'Publishing your changes to production...'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {phase === 'planning' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {phase === 'review' && plan && (
            <>
              {/* Summary badges */}
              <div className="flex gap-2">
                {plan.total_created > 0 && (
                  <Badge variant="default" className="bg-green-600">
                    {plan.total_created} new
                  </Badge>
                )}
                {plan.total_updated > 0 && (
                  <Badge variant="default" className="bg-blue-600">
                    {plan.total_updated} updated
                  </Badge>
                )}
                {plan.total_deleted > 0 && (
                  <Badge variant="destructive">
                    {plan.total_deleted} deleted
                  </Badge>
                )}
                {!plan.has_any_changes && (
                  <span className="text-sm text-muted-foreground">No changes detected.</span>
                )}
              </div>

              {plan.has_any_changes && (
                <div className="max-h-60 overflow-y-auto space-y-4 border rounded-md p-3">
                  <ChangedItemList
                    title="New Objects"
                    icon={<PlusCircle className="h-4 w-4 text-green-600" />}
                    items={collectItems('created')}
                  />
                  <ChangedItemList
                    title="Updated Objects"
                    icon={<Pencil className="h-4 w-4 text-blue-600" />}
                    items={collectItems('updated')}
                  />
                  <ChangedItemList
                    title="Deleted Objects"
                    icon={<Trash2 className="h-4 w-4 text-destructive" />}
                    items={collectItems('deleted')}
                  />
                </div>
              )}

              {/* Publish message */}
              <div className="space-y-2">
                <Label htmlFor="publish-message">Publish message</Label>
                <Textarea
                  id="publish-message"
                  placeholder="Describe your changes..."
                  value={publishMessage}
                  onChange={(e) => setPublishMessage(e.target.value)}
                  rows={3}
                />
              </div>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </>
          )}

          {phase === 'publishing' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogBody>

        {phase === 'review' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={!plan?.has_any_changes}>
              Confirm Publish
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
